/**
 * useSegmentHandlers - Segment operation handlers
 *
 * Provides handler functions for module segment operations:
 * - Creating segments (roof-attached and free-field)
 * - Copying segments with offset
 * - Refilling segments after resize/rotation
 * - Free-field polygon drawing
 */

import { useCallback } from 'react';
import { FREE_FIELD_POLYGON_ID } from './useModuleState';
import type { SegmentSelection } from './useModuleState';
import type { ModuleSegment, SolarModule } from '../../../types/geotiff.types';

// ============================================================================
// Types
// ============================================================================

export interface SegmentConfig {
  moduleSpacing: number;
  rowSpacing: number;
  targetCount: number | null;
}

export interface SegmentHandlersDeps {
  // State values
  selectedPolygon: number | null;
  isFreeFieldMode: boolean;
  freeFieldAzimuth: number;
  moduleSpacing: number;
  rowSpacingMultiplier: number;
  targetModuleCount: number | null;
  moduleSegments: { [polygonId: number]: ModuleSegment[] };
  modules: { [polygonId: number]: SolarModule[] };
  freeFieldPolygonVertices: [number, number][];

  // State setters
  setModuleSegments: React.Dispatch<React.SetStateAction<{ [polygonId: number]: ModuleSegment[] }>>;
  setModules: React.Dispatch<React.SetStateAction<{ [polygonId: number]: SolarModule[] }>>;
  setIsDrawingSegment: (value: boolean) => void;
  setDragPreviewRect: (rect: [number, number][] | null) => void;
  setPreviewModules: (modules: SolarModule[]) => void;
  setFreeFieldPolygonVertices: React.Dispatch<React.SetStateAction<[number, number][]>>;
  setSelectedSegment: (selection: { polygonId: number; index: number } | null) => void;

  // Utility functions
  calculateModulesForBox: (
    box: [number, number][],
    applyFillPercentage?: boolean,
    segmentRotation?: number,
    segmentConfig?: SegmentConfig,
    freeField?: boolean
  ) => SolarModule[];
  calculateSegmentRotation: (bounds: [number, number][]) => number;
}

export interface SegmentHandlers {
  // Segment creation
  handleSegmentCreated: (box: [number, number][], overrideRotation?: number) => void;

  // Free-field polygon drawing
  handleFreeFieldAddVertex: (vertex: [number, number]) => void;
  handleFreeFieldFinish: () => void;
  handleFreeFieldCancel: () => void;

  // Segment editing
  handleSegmentVerticesChange: (polygonId: number, segmentIndex: number, newCoords: [number, number][]) => void;
  refillSegment: (polygonId: number, segmentIndex: number, newBounds: [number, number][]) => void;

  // Segment operations
  handleCopySegment: (selectedSegment: SegmentSelection | null) => void;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useSegmentHandlers(deps: SegmentHandlersDeps): SegmentHandlers {
  const {
    selectedPolygon,
    isFreeFieldMode,
    freeFieldAzimuth,
    moduleSpacing,
    rowSpacingMultiplier,
    targetModuleCount,
    moduleSegments,
    modules,
    freeFieldPolygonVertices,
    setModuleSegments,
    setModules,
    setIsDrawingSegment,
    setDragPreviewRect,
    setPreviewModules,
    setFreeFieldPolygonVertices,
    setSelectedSegment,
    calculateModulesForBox,
    calculateSegmentRotation,
  } = deps;

  // ========== Segment Creation ==========

  const handleSegmentCreated = useCallback((box: [number, number][], overrideRotation?: number) => {
    // Determine which polygon ID to use
    const targetPolygonId = isFreeFieldMode ? FREE_FIELD_POLYGON_ID : selectedPolygon;

    if (targetPolygonId === null) {
      return;
    }

    // Determine segment rotation
    let segmentRotation: number;
    if (overrideRotation !== undefined) {
      segmentRotation = overrideRotation;
    } else if (isFreeFieldMode) {
      segmentRotation = freeFieldAzimuth;
    } else {
      segmentRotation = calculateSegmentRotation(box);
    }

    // Create segment config using current global settings
    const segmentConfig: SegmentConfig = {
      moduleSpacing,
      rowSpacing: rowSpacingMultiplier,
      targetCount: targetModuleCount
    };

    // Calculate modules for this segment
    const segmentModules = calculateModulesForBox(box, true, segmentRotation, segmentConfig, isFreeFieldMode);

    // Generate unique segment ID
    const existingSegments = moduleSegments[targetPolygonId] || [];
    const newSegmentId = existingSegments.length > 0
      ? Math.max(...existingSegments.map(s => s.id)) + 1
      : 0;

    // Assign segment ID to all modules
    const modulesWithSegmentId = segmentModules.map(m => ({
      ...m,
      segmentId: newSegmentId,
      freeField: isFreeFieldMode
    }));

    // Create the new segment
    const newSegment: ModuleSegment = {
      id: newSegmentId,
      bounds: box,
      modules: modulesWithSegmentId,
      config: segmentConfig,
      rotation: segmentRotation,
      azimuth: segmentRotation,
      freeField: isFreeFieldMode
    };

    // Add segment to state
    setModuleSegments(prev => ({
      ...prev,
      [targetPolygonId]: [...(prev[targetPolygonId] || []), newSegment]
    }));

    // Add modules to global modules list
    setModules(prev => ({
      ...prev,
      [targetPolygonId]: [...(prev[targetPolygonId] || []), ...modulesWithSegmentId]
    }));

    // Exit drawing mode and clear previews
    setIsDrawingSegment(false);
    setDragPreviewRect(null);
    setPreviewModules([]);
    setFreeFieldPolygonVertices([]);
  }, [
    selectedPolygon,
    isFreeFieldMode,
    freeFieldAzimuth,
    moduleSpacing,
    rowSpacingMultiplier,
    targetModuleCount,
    moduleSegments,
    setModuleSegments,
    setModules,
    setIsDrawingSegment,
    setDragPreviewRect,
    setPreviewModules,
    setFreeFieldPolygonVertices,
    calculateModulesForBox,
    calculateSegmentRotation,
  ]);

  // ========== Free-Field Polygon Drawing ==========

  const handleFreeFieldAddVertex = useCallback((vertex: [number, number]) => {
    setFreeFieldPolygonVertices(prev => [...prev, vertex]);
  }, [setFreeFieldPolygonVertices]);

  const handleFreeFieldFinish = useCallback(() => {
    if (freeFieldPolygonVertices.length >= 3) {
      const verticesCopy = [...freeFieldPolygonVertices];
      handleSegmentCreated(verticesCopy);
    }
    setFreeFieldPolygonVertices([]);
  }, [freeFieldPolygonVertices, handleSegmentCreated, setFreeFieldPolygonVertices]);

  const handleFreeFieldCancel = useCallback(() => {
    setFreeFieldPolygonVertices([]);
    setIsDrawingSegment(false);
  }, [setFreeFieldPolygonVertices, setIsDrawingSegment]);

  // ========== Segment Vertex Editing ==========

  const handleSegmentVerticesChange = useCallback((
    polygonId: number,
    segmentIndex: number,
    newCoords: [number, number][]
  ) => {
    const segments = moduleSegments[polygonId] || [];
    const segment = segments[segmentIndex];
    if (!segment) {
      console.error('Segment not found for vertex edit');
      return;
    }

    // Remove closing point if present
    const boundsWithoutClose = newCoords[0][0] === newCoords[newCoords.length - 1][0] &&
                               newCoords[0][1] === newCoords[newCoords.length - 1][1]
      ? newCoords.slice(0, -1)
      : newCoords;

    // Refill segment with new bounds
    refillSegmentInternal(polygonId, segmentIndex, boundsWithoutClose);
  }, [moduleSegments]);

  // ========== Refill Segment ==========

  const refillSegmentInternal = useCallback((
    polygonId: number,
    segmentIndex: number,
    newBounds: [number, number][]
  ) => {
    const segments = moduleSegments[polygonId] || [];
    const segment = segments[segmentIndex];
    const isFreeFieldSegment = polygonId === FREE_FIELD_POLYGON_ID || segment?.freeField;

    if (!segment) {
      console.error('Segment not found:', segmentIndex);
      return;
    }

    // Use segment's stored rotation
    const segmentRotation = segment.rotation || segment.azimuth || calculateSegmentRotation(newBounds);

    // Recalculate modules for new bounds
    const newModules = calculateModulesForBox(newBounds, true, segmentRotation, segment.config, isFreeFieldSegment);

    // Assign segment ID to new modules
    const modulesWithSegmentId = newModules.map(m => ({
      ...m,
      segmentId: segment.id,
      freeField: isFreeFieldSegment
    }));

    // Update segment with new bounds and modules
    const updatedSegment: ModuleSegment = {
      ...segment,
      bounds: newBounds,
      rotation: segmentRotation,
      modules: modulesWithSegmentId
    };

    // Update segments state
    const updatedSegments = [...segments];
    updatedSegments[segmentIndex] = updatedSegment;

    setModuleSegments(prev => ({
      ...prev,
      [polygonId]: updatedSegments
    }));

    // Remove old modules for this segment, add new ones
    const polygonModules = modules[polygonId] || [];
    const otherModules = polygonModules.filter(m => m.segmentId !== segment.id);

    setModules(prev => ({
      ...prev,
      [polygonId]: [...otherModules, ...modulesWithSegmentId]
    }));
  }, [moduleSegments, modules, setModuleSegments, setModules, calculateModulesForBox, calculateSegmentRotation]);

  // Public refillSegment that matches the original interface
  const refillSegment = useCallback((
    polygonId: number,
    segmentIndex: number,
    newBounds: [number, number][]
  ) => {
    refillSegmentInternal(polygonId, segmentIndex, newBounds);
  }, [refillSegmentInternal]);

  // ========== Copy Segment ==========

  const handleCopySegment = useCallback((selectedSegment: SegmentSelection | null) => {
    if (!selectedSegment || selectedSegment.polygonId === null) {
      console.warn('No segment selected to copy');
      return;
    }

    const { polygonId, index } = selectedSegment;
    const segments = moduleSegments[polygonId] || [];
    const segment = segments[index];

    if (!segment) {
      console.error('Segment not found:', index);
      return;
    }

    // Generate new unique segment ID
    const existingIds = segments.map(s => s.id);
    const newSegmentId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 0;

    // Calculate offset (2.5 meters south-east)
    const offsetLat = -2.5 / 111320;
    const avgLat = segment.bounds.reduce((sum, [lat]) => sum + lat, 0) / segment.bounds.length;
    const offsetLng = 2.5 / (111320 * Math.cos(avgLat * Math.PI / 180));

    // Copy bounds with offset
    const newBounds = segment.bounds.map(([lat, lng]) => [
      lat + offsetLat,
      lng + offsetLng
    ] as [number, number]);

    // Copy segment config
    const newSegmentConfig = { ...segment.config };

    // Calculate segment rotation
    const newSegmentRotation = calculateSegmentRotation(newBounds);

    // Calculate modules for new segment
    const newModules = calculateModulesForBox(newBounds, true, newSegmentRotation, newSegmentConfig);

    // Assign segment ID to new modules
    const modulesWithSegmentId = newModules.map(m => ({
      ...m,
      segmentId: newSegmentId
    }));

    // Create new segment
    const newSegment: ModuleSegment = {
      id: newSegmentId,
      bounds: newBounds,
      modules: modulesWithSegmentId,
      config: newSegmentConfig,
      rotation: newSegmentRotation
    };

    // Add new segment to segments state
    setModuleSegments(prev => {
      const updatedSegments = [...(prev[polygonId] || []), newSegment];
      return {
        ...prev,
        [polygonId]: updatedSegments
      };
    });

    // Add new modules to modules state
    setModules(prev => ({
      ...prev,
      [polygonId]: [...(prev[polygonId] || []), ...modulesWithSegmentId]
    }));

    // Select the newly copied segment
    setSelectedSegment({
      polygonId,
      index: segments.length // Index of the newly added segment
    });
  }, [moduleSegments, setModuleSegments, setModules, setSelectedSegment, calculateModulesForBox, calculateSegmentRotation]);

  return {
    handleSegmentCreated,
    handleFreeFieldAddVertex,
    handleFreeFieldFinish,
    handleFreeFieldCancel,
    handleSegmentVerticesChange,
    refillSegment,
    handleCopySegment,
  };
}

export default useSegmentHandlers;
